import { useEffect, useState, useMemo, type FormEvent } from "react"
import { createPortal } from "react-dom"
import {
  Save,
  FileText,
  Calendar,
  CheckCircle2,
  Clock3,
  Loader2,
  ImagePlus,
  ClipboardList,
  ChevronRight,
  User,
  X,
  Building2,
} from "lucide-react"
import { supabase, CleanReport, Property } from "../lib/supabase"
import { MediaCaptureModal } from "./MediaCaptureModal"
import { createEmptyChecklist, type ChecklistData } from "../lib/checklistTemplate"
import { isDemoSite } from "../lib/isDemoSite"
import { useAuth } from "./AuthProvider"
import { ChecklistEditModal } from "./ChecklistEditModal"
import { useToast } from "./Toast"
import { useCompanyConfig } from "../lib/useCompanyConfig"
import { RichTextEditor } from "./RichTextEditor"
import { GlitchSelect } from "./GlitchSelect"
import { GlitchDatePicker } from "./GlitchDatePicker"

type OperatorOption = { id: string; email: string; full_name: string | null; displayName: string }

const STATUS_PREVIEW_CLASSES: Record<string, string> = {
  Completed: "bg-emerald-500/20 text-emerald-200 border-emerald-400/30",
  "In Progress": "bg-blue-500/20 text-blue-200 border-blue-400/30",
  Scheduled: "bg-amber-500/20 text-amber-200 border-amber-400/30",
}

export type ReportEditFormVariant = "standalone" | "embedded"

export interface ReportEditFormProps {
  reportId: string
  onSave: (updatedReport: CleanReport, context?: { propertyName?: string }) => void
  onCancel: () => void
  variant?: ReportEditFormVariant
  /** For standalone: called when save fails (e.g. to navigate away). Not used in embedded. */
  onSaveError?: () => void
}

export function ReportEditForm({
  reportId,
  onSave,
  onCancel,
  variant = "embedded",
  onSaveError,
}: ReportEditFormProps) {
  const { toast } = useToast()
  const { config } = useCompanyConfig()
  const { isCreator, isSuperManager, allowedClientIds, role } = useAuth()
  const effectiveFullScope = isCreator || (role === "admin" && isSuperManager)
  const id = reportId

  const [report, setReport] = useState<Partial<CleanReport>>({
    title: "",
    status: "Scheduled",
    service_type: "Glass & Entry",
    activity_date: new Date().toISOString().split("T")[0],
    checklist_notes: "",
    photos: [],
    property_id: "",
  })
  const [properties, setProperties] = useState<Property[]>([])
  const [operators, setOperators] = useState<OperatorOption[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [operatorChoice, setOperatorChoice] = useState<"none" | string | "other">("none")
  const [mediaModalOpen, setMediaModalOpen] = useState(false)
  const [photosModalOpen, setPhotosModalOpen] = useState(false)
  const [checklist, setChecklist] = useState<ChecklistData>(() => createEmptyChecklist())
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({})
  const [checklistModalOpen, setChecklistModalOpen] = useState(false)
  const selectedStatus = report.status ?? "Completed"

  const categoryKeys = useMemo(() => Object.keys(checklist), [checklist])
  const defaultOpen = useMemo(() => {
    const o: Record<string, boolean> = {}
    categoryKeys.forEach((cat, i) => {
      o[cat] = i === 0
    })
    return o
  }, [categoryKeys])
  const isCategoryOpen = (cat: string) => openCategories[cat] ?? defaultOpen[cat] ?? false
  const setAllOpen = (open: boolean) =>
    setOpenCategories(categoryKeys.reduce((acc, c) => ({ ...acc, [c]: open }), {}))

  useEffect(() => {
    async function init() {
      try {
        let propsQuery = supabase
          .from("properties")
          .select("id, name, client_id, client:clients(name)")
          .order("name")
        if (effectiveFullScope) {
          const { data: scopeClients } = await supabase.from("clients").select("id").eq("is_demo", isDemoSite())
          const cids = (scopeClients || []).map((c) => c.id)
          propsQuery = cids.length > 0 ? propsQuery.in("client_id", cids) : propsQuery.in("client_id", [])
        } else if (allowedClientIds.length > 0) {
          propsQuery = propsQuery.in("client_id", allowedClientIds)
        } else {
          propsQuery = propsQuery.in("client_id", [])
        }
        const { data: propsData } = await propsQuery
        const propsList = (propsData || []) as unknown as Property[]
        setProperties(propsList)

        const { data: operatorsData } = await (supabase as any)
          .from("portal_operators")
          .select("id, email, full_name")
          .order("full_name")
        setOperators(
          (operatorsData || []).map((c: any) => ({
            id: c.id,
            email: c.email,
            full_name: c.full_name,
            displayName: (c.full_name || c.email).trim() || c.email,
          }))
        )

        const { data, error } = await (supabase as any)
          .from("activities")
          .select("*")
          .eq("id", id)
          .single()

        if (error) throw error
        if (data) {
          setReport(data as unknown as Partial<CleanReport>)
          const cl =
            data.checklist &&
            typeof data.checklist === "object" &&
            Object.keys(data.checklist).length > 0
              ? (data.checklist as ChecklistData)
              : createEmptyChecklist()
          setChecklist(cl)
          const name = data.operator_name
          if (name) {
            const match = (operatorsData || []).find(
              (c: any) => (c.full_name || c.email) === name || c.email === name
            )
            setOperatorChoice(match ? match.id : "other")
          }
          if (data.property_id && !effectiveFullScope && allowedClientIds.length > 0) {
            const inList = (propsData || []).some((p: { id: string }) => p.id === data.property_id)
            if (!inList) {
              const { data: singleProp } = await supabase
                .from("properties")
                .select("id, name, client_id, client:clients(name)")
                .eq("id", data.property_id)
                .single()
              if (singleProp) {
                setProperties((prev) =>
                  [...prev, singleProp as unknown as Property].sort((a, b) =>
                    a.name.localeCompare(b.name)
                  )
                )
              }
            }
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error)
        if (variant === "standalone" && onSaveError) onSaveError()
        else toast("Error loading report", "error")
      } finally {
        setLoading(false)
      }
    }

    init()
  }, [id, effectiveFullScope, allowedClientIds, variant, onSaveError])

  async function handleSave(e: FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      if (!report.property_id) {
        toast("Please select a property", "error")
        setSaving(false)
        return
      }
      const reportData = {
        ...report,
        checklist,
        photos: report.photos || [],
      }
      const { error } = await (supabase as any)
        .from("activities")
        .update(reportData)
        .eq("id", id)
      if (error) throw error
      const propertyName = properties.find((p) => p.id === report.property_id)?.name
      onSave(
        { ...reportData, id } as CleanReport,
        propertyName ? { propertyName } : undefined
      )
    } catch (error) {
      console.error("Error saving report:", error)
      toast("Error saving report", "error")
      if (variant === "standalone" && onSaveError) onSaveError()
    } finally {
      setSaving(false)
    }
  }

  const handleUploadedPhotos = (urls: string[]) => {
    setReport({ ...report, photos: [...(report.photos || []), ...urls] })
    setMediaModalOpen(false)
  }

  const photos = report.photos || []

  const removePhoto = (index: number) => {
    const newPhotos = [...(report.photos || [])]
    newPhotos.splice(index, 1)
    setReport({ ...report, photos: newPhotos })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-luster-500" />
      </div>
    )
  }

  const formContent = (
    <>
      <div className={variant === "embedded" ? "" : "max-w-lg mx-auto"}>
        <h2 className="text-sm font-mono font-bold text-white/90 mb-1">Edit Activity</h2>
        <p className="text-[11px] font-mono text-white/40 mb-6">Update the details below.</p>

        <form onSubmit={handleSave} className="space-y-5">
          {/* 1. Property selection */}
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-widest text-white/50 mb-1.5 flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Property selection *
            </label>
            <GlitchSelect
              value={report.property_id ?? null}
              onChange={(v) => setReport({ ...report, property_id: v })}
              options={properties.map((p) => ({
                value: p.id,
                label: `${p.name} (${(p as Property & { client?: { name: string } }).client?.name || "Unknown Client"})`,
              }))}
              placeholder="Select building…"
              icon={<Building2 className="w-3.5 h-3.5" />}
              aria-label="Property selection"
            />
          </div>

          {/* 2. Details card (title, service type, date, status, operator) */}
          <div className="space-y-4 rounded-2xl border border-white/[0.08] bg-white/[0.04] p-4">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-white/50 mb-1.5 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Clean title
              </label>
              <input
                required
                type="text"
                value={report.title}
                onChange={(e) => setReport({ ...report, title: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-white/[0.08] bg-white/[0.04] text-white/80 font-mono text-[11px] focus:outline-none focus:ring-2 focus:ring-teal-400/30 placeholder:text-white/40"
                placeholder="e.g. Weekly Office Clean – Suite 200"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-white/50 mb-1.5">Service type</label>
                <GlitchSelect
                  value={report.service_type ?? null}
                  onChange={(v) => setReport({ ...report, service_type: v as CleanReport["service_type"] })}
                  options={
                    (config?.service_types?.length
                      ? config.service_types
                      : ["Glass & Entry", "Surface Precision", "Floor Care", "Restroom Reset", "Smart Oversight", "Standard Maintenance"]
                    ).map((t) => ({ value: t, label: t }))
                  }
                  aria-label="Service type"
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-white/50 mb-1.5 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Date conducted
                </label>
                <GlitchDatePicker
                  value={report.activity_date || ""}
                  onChange={(v) => setReport({ ...report, activity_date: v })}
                  aria-label="Date conducted"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-white/50 mb-1.5">Status</label>
                <GlitchSelect
                  value={selectedStatus}
                  onChange={(v) => setReport({ ...report, status: v as CleanReport["status"] })}
                  options={[
                    { value: "Scheduled", label: "Scheduled" },
                    { value: "In Progress", label: "In Progress" },
                    { value: "Completed", label: "Completed" },
                  ]}
                  aria-label="Status"
                />
                <div
                  className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-mono font-bold uppercase tracking-wider ${STATUS_PREVIEW_CLASSES[selectedStatus] ?? "bg-white/[0.04] text-white/50 border-white/[0.12]"}`}
                >
                  {selectedStatus === "Completed" ? (
                    <CheckCircle2 className="w-3 h-3 shrink-0" />
                  ) : selectedStatus === "Scheduled" ? (
                    <Calendar className="w-3 h-3 shrink-0" />
                  ) : (
                    <Clock3 className="w-3 h-3 shrink-0" />
                  )}
                  <span>{selectedStatus}</span>
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-white/50 mb-1.5 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Operator
                </label>
                <GlitchSelect
                  value={operatorChoice}
                  onChange={(v) => {
                    setOperatorChoice(v as "none" | string | "other")
                    if (v === "none") setReport({ ...report, operator_name: undefined })
                    else if (v === "other")
                      setReport({ ...report, operator_name: report.operator_name || "" })
                    else {
                      const c = operators.find((x) => x.id === v)
                      setReport({ ...report, operator_name: c ? c.displayName : undefined })
                    }
                  }}
                  options={[
                    { value: "none", label: "— None" },
                    ...operators.map((c) => ({ value: c.id, label: c.displayName })),
                    { value: "other", label: "Other (enter name)" },
                  ]}
                  icon={<User className="w-3.5 h-3.5" />}
                  aria-label="Operator"
                />
                {operatorChoice === "other" && (
                  <input
                    type="text"
                    value={report.operator_name || ""}
                    onChange={(e) => setReport({ ...report, operator_name: e.target.value })}
                    className="w-full mt-2 px-3 py-2 rounded-lg border border-white/[0.08] bg-white/[0.04] text-white/80 font-mono text-[11px] focus:outline-none focus:ring-2 focus:ring-teal-400/30 placeholder:text-white/40"
                    placeholder="Staff member name"
                  />
                )}
              </div>
            </div>
          </div>

          {/* 3. Service narrative / Notes */}
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.04] p-4">
            <label className="block text-[11px] font-bold uppercase tracking-widest text-white/50 mb-1.5 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Service narrative / Notes
            </label>
            <div className="mt-1">
              <RichTextEditor
                content={report.checklist_notes || ""}
                onChange={(html) => setReport({ ...report, checklist_notes: html })}
                placeholder="Activity details, issues found, supplies needed..."
              />
            </div>
          </div>

          {/* 4. Photos — section card opens modal */}
          <button
            type="button"
            onClick={() => setPhotosModalOpen(true)}
            className="w-full flex items-center justify-between gap-3 p-4 rounded-2xl border border-white/[0.08] bg-white/[0.04] hover:bg-white/[0.08] transition-colors text-left"
          >
            <span className="flex items-center gap-2 text-[11px] font-mono font-bold text-white/80">
              <ImagePlus className="w-5 h-5 text-teal-400/90 shrink-0" />
              Photos
            </span>
            <span className="text-[10px] font-mono text-white/40">
              {photos.length} photo{photos.length !== 1 ? "s" : ""}
            </span>
            <ChevronRight className="w-5 h-5 text-white/40 shrink-0" aria-hidden />
          </button>

          {/* 5. Protocol checklist — section card opens modal */}
          <button
            type="button"
            onClick={() => setChecklistModalOpen(true)}
            className="w-full flex items-center justify-between gap-3 p-4 rounded-2xl border border-white/[0.08] bg-white/[0.04] hover:bg-white/[0.08] transition-colors text-left"
          >
            <span className="flex items-center gap-2 text-[11px] font-mono font-bold text-white/80">
              <ClipboardList className="w-5 h-5 text-teal-400/90 shrink-0" />
              Protocol checklist
            </span>
            <span className="text-[10px] font-mono text-white/40">
              {categoryKeys.length} categor{categoryKeys.length === 1 ? "y" : "ies"} ·{" "}
              {categoryKeys.reduce((acc, cat) => acc + (checklist[cat]?.tasks?.length ?? 0), 0)} tasks
            </span>
            <ChevronRight className="w-5 h-5 text-white/40 shrink-0" aria-hidden />
          </button>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 py-2 px-3 rounded-lg border border-white/[0.08] bg-white/[0.04] text-white/50 text-[11px] font-mono hover:bg-white/[0.08] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg border border-teal-400/25 bg-teal-500/20 text-teal-400 text-[11px] font-mono font-bold uppercase tracking-widest hover:bg-teal-500/30 transition-colors disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              Save changes
            </button>
          </div>
        </form>
      </div>

      <MediaCaptureModal
        open={mediaModalOpen}
        onClose={() => setMediaModalOpen(false)}
        onPhotosAdded={handleUploadedPhotos}
        uploadPrefix={id}
      />

      {/* Photos modal — same UX as operator form */}
      {photosModalOpen &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setPhotosModalOpen(false)}
              aria-hidden
            />
            <div className="relative w-full max-w-lg max-h-[90vh] flex flex-col bg-stone-950/95 backdrop-blur-2xl rounded-2xl shadow-xl border border-white/[0.08] overflow-hidden">
              <div className="flex items-center justify-between shrink-0 px-4 py-3 border-b border-white/[0.06]">
                <h3 className="text-sm font-mono font-bold text-white/90 flex items-center gap-2">
                  <ImagePlus className="w-5 h-5 text-teal-400/90" />
                  Photos
                </h3>
                <button
                  type="button"
                  onClick={() => setPhotosModalOpen(false)}
                  className="p-1.5 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/10"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
                <button
                  type="button"
                  onClick={() => setMediaModalOpen(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-4 rounded-xl border border-dashed border-white/[0.08] text-white/40 font-mono text-[11px] hover:border-teal-400/30 hover:text-teal-400/80 transition-colors"
                >
                  <ImagePlus className="w-5 h-5" />
                  Add photos (camera or gallery)
                </button>
                {photos.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {photos.map((url, i) => (
                      <div
                        key={i}
                        className="relative aspect-square rounded-lg overflow-hidden bg-white/[0.04] border border-white/[0.08] group"
                      >
                        <img src={url} alt="" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removePhoto(i)}
                          className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity text-white text-[11px] font-mono"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="shrink-0 p-4 border-t border-white/[0.06]">
                <button
                  type="button"
                  onClick={() => setPhotosModalOpen(false)}
                  className="w-full py-2 px-4 rounded-lg border border-teal-400/25 bg-teal-500/20 text-teal-400 text-[11px] font-mono font-bold uppercase tracking-widest hover:bg-teal-500/30 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {checklistModalOpen && (
        <ChecklistEditModal
          checklist={checklist}
          setChecklist={setChecklist}
          openCategories={openCategories}
          setOpenCategories={setOpenCategories}
          onClose={() => setChecklistModalOpen(false)}
        />
      )}
    </>
  )

  if (variant === "embedded") {
    return <div className="max-h-full overflow-y-auto">{formContent}</div>
  }

  return formContent
}
